import { api_configuration } from "../../common";
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import getQueryInstanceKey from "../state/getQueryInstanceKey";
import { isDefined } from "../validation";
import { canDownloadFilesQuery } from "../widgets/instructions/fileUpload";
import { client_state } from "./ClientState";
import api_client_state = api_configuration.types.client_state.alan_api;
import client_state_decorator = api_configuration.types.client_state.alan_api;
import createColumnsFromColumnsBinding from "./createColumnsFromColumnsBinding";

export function forQuery(
	context: api_client_state.Cnode,
	query_definition: api_views.Cqueries,
	on_added: (query: api_client_state.Cquery__client_state) => void
): api_client_state.Cquery__client_state {
	const view_context_name = isDefined(query_definition.parent.parent.key);
	const view_name = query_definition.parent.key;
	const query_name = query_definition.key;
	const query_instance_name = getQueryInstanceKey(query_definition);
	const can_download_files = canDownloadFilesQuery(query_definition);

	return context.properties.queries.switchOnEntryExists(
		query_instance_name,
		function ($) {
			return $.properties.query;
		},
		function () {
			const added_query = context.properties.queries.create(query_instance_name, {
				query: {
					"view definition context": view_context_name,
					"view definition": view_name,
					definition: query_name,
					"use for": ["query", {}],
					"delay query": ["yes", {}],
					"has selected entries": "no",
					"can download files": can_download_files ? "yes" : "no",
					"entries are editable": client_state.properties.session.properties.status.switch<
						client_state_decorator.Tquery__client_state["entries are editable"]
					>({
						"not authenticated": ["no", {}],
						authenticated: function () {
							const query_path = query_definition.properties.context.cast("node").properties.path;
							return query_path.output
								.result_node()
								.input.application_node()
								.input.value_source()
								.switch<client_state_decorator.Tquery__client_state["entries are editable"]>({
									derived: "no",
									base: (): client_state_decorator.Tquery__client_state["entries are editable"] => [
										"yes",
										{
											"is importing": ["no", {}],
											"can create entry": ["no", {}],
										},
									],
								});
						},
					}),
					"has columns": query_definition.properties.has_columns.switch<
						client_state_decorator.Tquery__client_state["has columns"]
					>({
						yes: ($) => ["yes", { first: $.properties.first.entry }],
						no: ["no", {}],
					}),
					columns: createColumnsFromColumnsBinding(query_definition),
					"show all": "no",
					"has result": "no",
					"is busy querying": ["no", { "request status": ["success", {}] }],
					filters: query_definition.properties.filters.properties.filters.map(() => {
						return { active: "yes" };
					}),
				},
			}).properties.query;
			on_added(added_query);
			return added_query;
		}
	);
}

function entriesAreEditableState(
	is_editable: boolean
): client_state_decorator.Tquery__client_state["entries are editable"] {
	return is_editable
		? ["yes", { "is importing": ["no", {}], "can create entry": ["yes", { in: ["candidates", {}] }] }]
		: ["no", {}];
}

function createCandidatesQuery(
	query_definition: api_views.Cqueries,
	context_node: api_client_state.Cnode,
	entries_are_editable = false
): client_state_decorator.Tquery__client_state {
	const view_context_name = isDefined(query_definition.parent.parent.key);
	const view_name = query_definition.parent.key;
	const query_name = query_definition.key;
	const can_download_files = canDownloadFilesQuery(query_definition);

	return {
		"view definition context": view_context_name,
		"view definition": view_name,
		definition: query_name,
		"use for": ["candidates", {}],
		"has selected entries": "no",
		"can download files": can_download_files ? "yes" : "no",
		"delay query": ["yes", {}],
		"entries are editable": entriesAreEditableState(entries_are_editable),
		"has columns": query_definition.properties.has_columns.switch<
			client_state_decorator.Tquery__client_state["has columns"]
		>({
			yes: ($) => ["yes", { first: $.properties.first.entry }],
			no: ["no", {}],
		}),
		columns: createColumnsFromColumnsBinding(query_definition),
		"show all": "no",
		"has result": "no",
		"is busy querying": ["no", { "request status": ["success", {}] }],
		filters: query_definition.properties.filters.properties.filters.map(() => {
			return {
				active: "yes",
			};
		}),
	};
}

function updateEditable(query: api_client_state.Ccandidates__use_for__query, entries_editable: boolean | undefined) {
	if (entries_editable === undefined) {
		return query.parent;
	}
	switch (query.parent.properties.entries_are_editable.state.name) {
		case "yes":
			if (!entries_editable) {
				query.parent.properties.entries_are_editable.set("no", {});
			}
			break;
		case "no":
			if (entries_editable) {
				query.parent.properties.entries_are_editable.set("yes", {
					"is importing": ["no", {}],
					"can create entry": [
						"yes",
						{
							in: ["candidates", {}],
						},
					],
				});
			}
			break;
		default:
			unexpectedState(query.parent.properties.entries_are_editable.state);
	}
	return query.parent;
}

export function forReference(
	navigable_text: api_client_state.Cyes__navigable,
	query_definition: api_views.Cqueries,
	entries_are_editable: boolean | undefined
): api_client_state.Cquery__client_state {
	const query_instance_name = getQueryInstanceKey(query_definition);
	return navigable_text.properties.queries.switchOnEntryExists(
		query_instance_name,
		(q) => updateEditable(q.properties.query.properties.use_for.cast("candidates"), entries_are_editable),
		() =>
			navigable_text.properties.queries.create(query_instance_name, {
				query: createCandidatesQuery(
					query_definition,
					navigable_text.parent.parent.parent.parent,
					entries_are_editable
				),
			}).properties.query
	);
}
